/**
 * Dockview drag vs native WebContentsView (架构方案 §5.7 / 见 22).
 *
 * The drop highlight is DOM. A WebContentsView is composited above it, so the
 * gray target never appears over a built-in browser page unless that page is
 * taken out of the window for the whole gesture. The JPEG already painted
 * under the view is what shows through. This does not enter the modal
 * suppression path: that one only hides the view, and a hidden view still
 * attached to the window keeps the pointer for the drag's release.
 *
 * Call {@link beginDockviewNativeContentDrag} from a capture-phase `dragstart`
 * listener registered immediately after the locked-area veto. The veto sets
 * `defaultPrevented`, and a cancelled drag never emits `dragend`, so a vetoed
 * drag must not detach. The matching `dragend` is listened on the handle
 * itself: after a drop the tab may already be out of the document, and a
 * `document` listener never sees that event.
 */

/** Tab header or the strip's empty space — the two dockview drag handles. */
export const DOCKVIEW_DRAG_HANDLE_SELECTOR = '.dv-tab, .dv-void-container';

type NativeDragBridge = {
  setHostSuppressed?(suppressed: boolean): void;
};

type DockviewDragStartEvent = {
  defaultPrevented: boolean;
  target: EventTarget | null;
};

type DragHandle = EventTarget & {
  addEventListener(type: 'dragend', listener: () => void): void;
  removeEventListener(type: 'dragend', listener: () => void): void;
};

/** Detach every native view in this window before `dragstart` returns. */
function hideNativeContentForDockviewDrag(
  bridge: NativeDragBridge | undefined,
): void {
  bridge?.setHostSuppressed?.(true);
}

/**
 * Put detached views back on the window. A modal's per-instance suppression
 * still decides whether they are shown.
 */
function reattachNativeContentAfterDockviewDrag(
  bridge: NativeDragBridge | undefined,
): void {
  bridge?.setHostSuppressed?.(false);
}

/**
 * Detach for one accepted dockview drag, and attach again when that handle
 * fires `dragend`. A vetoed drag (`defaultPrevented`) is left alone.
 * `dragend` that re-enters while detach is still inside this call is ignored:
 * `setHostSuppressed` is synchronous and can pump a nested event.
 */
export function beginDockviewNativeContentDrag(
  event: DockviewDragStartEvent,
  bridge: NativeDragBridge | undefined = readNativeContentsBridge(),
): void {
  const handle = acceptedDragHandle(event);
  if (!handle) {
    return;
  }
  let arming = true;
  const end = (): void => {
    if (arming) {
      return;
    }
    handle.removeEventListener('dragend', end);
    reattachNativeContentAfterDockviewDrag(bridge);
  };
  handle.addEventListener('dragend', end);
  hideNativeContentForDockviewDrag(bridge);
  arming = false;
}

export function isAcceptedDockviewDragStart(
  event: DockviewDragStartEvent,
): boolean {
  return acceptedDragHandle(event) !== undefined;
}

function acceptedDragHandle(
  event: DockviewDragStartEvent,
): DragHandle | undefined {
  if (event.defaultPrevented || event.target === null) {
    return undefined;
  }
  const target = event.target;
  if (!hasClosest(target)) {
    return undefined;
  }
  const handle = target.closest(DOCKVIEW_DRAG_HANDLE_SELECTOR);
  if (!isDragHandle(handle)) {
    return undefined;
  }
  return handle;
}

function hasClosest(
  target: EventTarget,
): target is EventTarget & { closest(selector: string): unknown } {
  return (
    typeof target === 'object' &&
    'closest' in target &&
    typeof target.closest === 'function'
  );
}

function isDragHandle(value: unknown): value is DragHandle {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  return (
    'addEventListener' in value &&
    typeof value.addEventListener === 'function' &&
    'removeEventListener' in value &&
    typeof value.removeEventListener === 'function'
  );
}

function readNativeContentsBridge(): NativeDragBridge | undefined {
  return (
    globalThis as {
      hibroNativeContents?: NativeDragBridge;
    }
  ).hibroNativeContents;
}
